<template>
  <div class="bui-entity-listing-page">
    <component
      :is="ItemsLister"
      ref="itemsListerRef"
      :singleSelect="singleSelect"
      :selectMode="selectMode"
      :disableTableScroll="disableTableScroll"
      :MetaDataOptionsComponent="MetaDataOptionsComponent"
      v-bind="{
        ...(areaAccessName ? { areaAccessName } : null),
        ...(itemsListerBind || {}),
        useBuiChrome: true,
        buiPrimaryAction,
        onPrimaryAction: handlePrimary,
      }"
    >
      <template #toolbar-title-after>
        <slot name="title-after" />
      </template>
    </component>

    <Teleport to="body">
      <div v-if="createDialogOpen" class="bui-tbl-confirm-overlay" role="dialog" aria-modal="true" data-testid="list-create-dialog">
        <div class="bui-tbl-confirm-card create-card">
          <button class="close-x" type="button" aria-label="Close" data-testid="dialog-close" @click="closeCreateDialog">
            <PhX :size="20" weight="bold" />
          </button>
          <h3 class="title">Create {{ entityItemName || 'Item' }}</h3>
          <component
            :is="CreateItemFormComponent"
            v-if="CreateItemFormComponent"
            @cancel="closeCreateDialog"
            @success="onCreateSuccess"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, ref } from 'vue';
import { PhPlus, PhX } from '@phosphor-icons/vue';
import useUser from '@/composables/user';

const props = withDefaults(
  defineProps<{
    entityItemsNamePlural: string;
    entityItemName?: string;
    CreateItemFormComponent?: any;
    ItemsLister: any;
    itemsListerBind?: any;
    hideDefaultTitle?: boolean;
    customTitle?: string;
    singleSelect?: boolean;
    selectMode?: boolean;
    createItemPage?: boolean;
    areaAccessName?: string;
    MetaDataOptionsComponent?: any;
    disableTableScroll?: boolean;
  }>(),
  { hideDefaultTitle: false, singleSelect: false, selectMode: false, createItemPage: false, disableTableScroll: false },
);

const emit = defineEmits<{ create: [] }>();

const user = useUser();

provide('simpleEntityListingPageConfig', {
  entityItemsNamePlural: props.entityItemsNamePlural,
  entityItemName: props.entityItemName,
});

const itemsListerRef = ref();

const buiPrimaryAction = computed(() => {
  const showCreate =
    !!props.CreateItemFormComponent &&
    !!props.areaAccessName &&
    user.hasCreateAccessToArea(props.areaAccessName);
  const showCreateRoute = !!props.createItemPage;
  if (!showCreate && !showCreateRoute) return null;
  const baseLabel = props.hideDefaultTitle && props.customTitle
    ? props.customTitle
    : `Create ${props.entityItemName || 'Item'}`;
  return { label: baseLabel, icon: PhPlus };
});

const createDialogOpen = ref(false);

function handlePrimary() {
  if (props.createItemPage) {
    emit('create');
    return;
  }
  if (props.CreateItemFormComponent) {
    createDialogOpen.value = true;
  }
}

function closeCreateDialog() {
  createDialogOpen.value = false;
}

function onCreateSuccess() {
  setTimeout(() => {
    closeCreateDialog();
  }, 1000);
}
</script>

<style lang="scss" scoped>
.bui-entity-listing-page {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  min-height: 100%;
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px);
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  box-sizing: border-box;

  @media (max-width: 768px) {
    --bui-page-pad-y: 8px;
    --bui-page-pad-x: 8px;
    --bui-page-section-gap: 6px;
    /* Compact chrome for lister + table (inherited by descendants). */
    --bui-ter-h: 32px;
    --bui-ter-pad: 6px;
    --bui-cta-h: 32px;
    --bui-tbl-head-h: 30px;
    --bui-tbl-cell-pad-x: 6px;
    --bui-tbl-cell-gap: 8px;
    --bui-tbl-row-min-h: 42px;
    --bui-tbl-row-max-h: 62px;
    --bui-tbl-row-min-h-preview: 62px;
    --bui-tbl-row-font-size: 11px;
    --bui-tbl-row-line-height: 14px;
    --bui-tbl-head-font-size: 9px;
    --bui-tbl-head-line-height: 11px;
    --bui-tbl-thumb-size: 52px;
    --bui-tbl-thumb-inner: 48px;
    --bui-chk-size: 15px;
  }
}

.create-card {
  position: relative;

  .close-x {
    position: absolute;
    top: 12px;
    right: 12px;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: #2a2969;
    padding: 4px;
    border-radius: 4px;

    &:hover { background: rgba(42, 41, 105, 0.06); }
  }
}
</style>
